//#region Imports
// ** React Imports
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

// ** MUI Components
import { Box, Typography, IconButton, Popover, } from "@mui/material";

// ** Custom Component Imports
import CustomChip from "../../../components/mui/chip";

// ** Icon Imports
import Icon from "../../../components/icon";

// ** Dialog Imports
import RefundDialog from '../component/Refund';
import { getPayments } from "./PaymentLogic";
import { usePaymentPermission } from "../permissions/PaymentPermissions";
import dayjs from "dayjs";
import  { formatUSCurrency } from "components/CurrencyFormat";
//#endregion Imports

//#region Styled Components
const dropdownItemsStyle = {
  px: 6,
  py: 2,
  "&:hover": {
    backgroundColor: "action.hover",
    cursor: "pointer",
  },
};

const OptionsButton = ({RefundProps, RefundData}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  const { openRefundDlg, handleOpenRefundDlg, handleCloseRefundDlg, refundData } = RefundProps;

  return (
    <>
      <IconButton onClick={handleClick}>
        <Icon icon="tabler:dots-vertical" />
      </IconButton>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Box sx={{ py: 2 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            {RefundData && RefundData?.status !== "Refunded" && (
              <Typography  
                variant="h6" 
                sx={dropdownItemsStyle} 
                onClick={() => {
                  handleClose();
                  handleOpenRefundDlg(RefundData);                  
                }}
              >              
                Refund
              </Typography>
            )}

            <Typography variant="h6" sx={dropdownItemsStyle}>
              Send Receipt
            </Typography>
            <Typography variant="h6" sx={dropdownItemsStyle}>
              Resend Receipt
            </Typography>
          </Box>
        </Box>
      </Popover>
    </>
  );
};
//#endregion Styled Components

export const useIndexLogic = () => {
  //#region Permissions
  const { PaymentPermissionProps } = usePaymentPermission();
  const {
    hasPaymentGetPermission,
  } = PaymentPermissionProps;
  //#endregion Permissions

  //#region Hooks
  const navigate = useNavigate();
  //#endregion

  //#region Constants
  const contentHeightFixed = false;
  //#endregion End of Constants

  //#region States
  const [loading, setLoading] = useState(true);
  const [openInvoicePayment, setOpenInvoicePayment] = useState(false);
  const [openAdvPayment, setOpenAdvPayment] = useState(false);
  const [data, setData] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [paginationText, setPaginationText] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [openTagManagementDlg, setOpenTagManagementDlg] = useState(false);
  const [openDetail, setOpenDetail] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState("");
  const [entity, setEntity] = useState("");
  const [paymentID, setPaymentID] = useState("");
  const tagManagementData = { title: "Tag Management" };
  const [openRefundDlg, setOpenRefundDlg] = useState(false);
  const [refundData, setRefundData] = useState([]);
  const [openPaymentReceipt, setOpenPaymentReceipt] = useState(false);
  const [selectedPaymentRow, setSelectedPaymentRow] = useState({});
  
  //#endregion end of states

  //#region Handlers and methods
  const customerID = 0;
  const requestID = 0;

  const formatPrice = (val) => {
    if (val === null || val === undefined || val === "") return "N/A";
    const num = typeof val === "number" ? val : Number(val);
    return isNaN(num)
      ? "N/A"
      : `$${num.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  };
    
  const fetchPayments = useCallback(async (invoiceID = 0) => {
    if(!hasPaymentGetPermission) return;

    setLoading(true);
    getPayments()
      .then((res) => {
        const raw = res || [];

        // Map API fields to grid fields
        const mapped = raw.map((item, idx) => ({
          id: item.paymentID, // unique row id for DataGrid
          PAYpaymentID: item.paymentStatus === "Refunded" ? `REF - ${item.paymentID}` : `PAY - ${item.paymentID}`,
          WO_ID: item.invoiceID
            ? `INV-${item.invoiceID}`
            : item.requestID
              ? (item.requestType === 'WorkOrder' ? `WO-${item.requestID}` : `AST-${item.requestID}`)
              : "",
          customerName: item.customerName || "",
          createdOn: item.createDate ? dayjs(item.createDate).format("MM/DD/YYYY") : "",
          createdBy: item.createdBy || "",
          dueDate: item.dueDate ? dayjs(item.dueDate).format("MM/DD/YYYY") : "",
          item_paymentDate: item.paymentDate ? dayjs(item.paymentDate).format("MM/DD/YYYY") : "",
          paymentMethod: item.paymentMethod || "",
          amount: item.amount !== undefined ? item.amount : "",
          status: item.paymentStatus,
          entity: item.paymentType === "Invoice" ? "Invoice" : "Advance",
          // add any other fields if needed
          ...item
        }));
       
        // setData(mapped);
        // console.log(mapped)
        const invoiceID_Num = Number(invoiceID) || 0;

        const forThisInvoice = invoiceID_Num > 0
        ? mapped.filter(item => item.invoiceID === invoiceID_Num)
        : mapped;

        // 1) sort newest-first
        const sorted = forThisInvoice.sort((a,b) => b.id - a.id);
        // console.log(invoiceID)
        // console.log(sorted)

        // 2) set both data & filteredRows here, once
        setData(sorted);
        setFilteredRows(sorted);

        setLoading(false);
        // setFilteredRows(mapped.sort((a, b) => b.id - a.id));
      })
      .catch(() => {
        setData([]);
        setFilteredRows([]);
        setLoading(false);
        // Optionally show error
      })
    }, [hasPaymentGetPermission]);


    // useEffect(() => {
    //   if (hasPaymentGetPermission) {
    //     fetchPayments(invoiceID);   // only fetch once, with the real invoiceID
    //   }
    // }, [fetchPayments, invoiceID, hasPaymentGetPermission]);

  // useEffect(() => {
  //   setFilteredRows(() =>
  //     data.filter((row) =>
  //       Object.values(row).some(
  //         (value) =>
  //           value &&
  //           value.toString().toLowerCase().includes(searchText.toLowerCase())
  //       )
  //     )
  //   );
  // }, [searchText]);

  useEffect(() => {
    if (!searchText) {
      // no filter → show all
      setFilteredRows(data);
    } else {
      const lower = searchText.toLowerCase();
      const filtered = data.filter(row =>
        Object.values(row).some(val =>
          val?.toString().toLowerCase().includes(lower)
        )
      );
      setFilteredRows(filtered);
    }
  }, [searchText, data]);

  useEffect(() => {
    const total = filteredRows.length;
    let start = paginationModel.page * paginationModel.pageSize + 1;
    let end = Math.min(
      (paginationModel.page + 1) * paginationModel.pageSize,
      total
    );

    if (total === 0) {
      setPaginationText("0-0 of 0");
      return;
    }

    if (paginationModel.pageSize === -1) {
      start = 1;
      end = total;
    }

    setPaginationText(`${end} of ${total}`);
  }, [filteredRows, paginationModel.page, paginationModel.pageSize]);

  const handleOpenDetailDlg = (status, entity, paymentID, row) => {
    setEntity(entity);
    setPaymentStatus(status);
    setPaymentID(paymentID);
    setOpenDetail(true);
    setSelectedPaymentRow(row);
  };
  const handleCloseDetailDlg = () => setOpenDetail(false);

  const handleOpenInvoicePayment = () => setOpenInvoicePayment(true);
  const handleCloseInvoicePayment = () => setOpenInvoicePayment(false);

  const handleOpenAdvPayment = () => setOpenAdvPayment(true);
  const handleCloseAdvPayment = () => setOpenAdvPayment(false);

  const handleOpenTagManagementDlg = () => setOpenTagManagementDlg(true);
  const handleCloseTagManagementDlg = () => setOpenTagManagementDlg(false);

  const handleClearSelection = () => {
    setRowSelectionModel([]);
    setSelectedRows(0);
  };

  const handleGridSelection = (selectedRows) => setSelectedRows(selectedRows);

  const handleSearch = (val) => setSearchText(val);

  const handleClearSearch = () => setSearchText("");

  const handleOpenRefundDlg = (RefundData) => { 
    setRefundData(RefundData);
    setOpenRefundDlg(true); 
  }
  const handleCloseRefundDlg = () => setOpenRefundDlg(false);

  const handleOpenPaymentReceipt = () => setOpenPaymentReceipt(true);
  const handleClosePaymentReceipt = () => setOpenPaymentReceipt(false);

  const handlePaginationModel = (model) =>{
    if(model.pageSize === -1){
      setPaginationModel({
        page: 0,
        pageSize: -1,
      })
    }else{
      setPaginationModel(model);
    }  
  }
 
  
  //#endregion end of handlers and methods

  //#region Columns Configurations
  const columns = [
    {
      // flex: 0.1,
      field: "item_paymentDate",
      headerName: "Payment Date",
      minWidth: 100,
      renderCell: ({ row }) => {
        const { item_paymentDate } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>{item_paymentDate}</Typography>
          </Box>
        );
      },
    },
    {
      // flex: 0.1,
      field: "PAYpaymentID",
      minWidth: 100,
      headerName: "Payment ID",
      renderCell: ({ row }) => {
        const { PAYpaymentID, status, entity } = row;
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              cursor: "pointer",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
            onClick={() => handleOpenDetailDlg(status, entity, row.id, row)}
          >
            <Typography sx={{ flexGrow: 1 }}>{PAYpaymentID}</Typography>
          </Box>
        );
      },
    },      
    {
      // flex: 0.1,
      field: "WO_ID",
      minWidth: 100,
      headerName: "Linked To",
      renderCell: ({ row }) => {
        const { WO_ID } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>{WO_ID}</Typography>
          </Box>
        );
      },
    },
    {
      // flex: 0.1,
      field: "customerName",
      minWidth: 120,
      headerName: "Customer Name",
      renderCell: ({ row }) => {
        const { customerName , customerID } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" ,
             cursor: "pointer",
            "&:hover": {
              textDecoration: "underline",
          }, }} 
          onClick={() => navigate(`/customers/detail/${customerID}`)}>
            <Typography sx={{ flexGrow: 1 }}>{customerName}</Typography>
          </Box>
        );
      },
    },
    {
      // flex: 0.1,
      field: "createdOn",
      minWidth: 100,
      headerName: "Created On",
      renderCell: ({ row }) => {
        const { createdOn } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>{createdOn}</Typography>
          </Box>
        );
      },
    },
    {
      // flex: 0.1,
      field: "createdBy",
      headerName: "Created By",
      minWidth: 120,
      renderCell: ({ row }) => {
        const { createdBy } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>{createdBy}</Typography>
          </Box>
        );
      },
    },
    {
      // flex: 0.1,
      field: "paymentMethod",
      headerName: "Payment Method",
      minWidth: 100,
      renderCell: ({ row }) => {
        const { paymentMethod } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>{paymentMethod}</Typography>
          </Box>
        );
      },
    },
    {
      // flex: 0.1,
      field: "amount",
      headerName: "Amount",
      minWidth: 80,
      renderCell: ({ row }) => {
        const { amount } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>{formatPrice(amount)}</Typography>
          </Box>
        );
      },
    },
    {
      // flex: 0.1,
      field: "status",
      headerName: "Status",
      minWidth: 80,
      renderCell: ({ row }) => {
        const { status } = row;

        return (
          <CustomChip
            rounded
            size="small"
            skin="light"
            sx={{ lineHeight: 1 }}
            color={
              status === "Paid"
                ? "success"
                : status === "Pending"
                ? "warning"
                : status === "Refunded"?
                "error"
                : "secondary"
            } // Use color names: primary, secondary, error, warning, info, success
            label={status}
          />
        );
      },
    },
    {
      // flex: 0.1,
      field: "actions",
      headerName: "Actions",
      sortable: false,
      minWidth: 80,
      renderCell: ({ row }) => {

        return (
          <OptionsButton RefundProps={RefundProps} RefundData={row} />
        );
      },
    },
  ];
  //#endregion Columns Configurations

  //#region Props
  const IndexProps = {
    columns,
    contentHeightFixed,
    openDetail,
    entity,
    paymentStatus,
    paymentID,
    handleCloseDetailDlg,
    openInvoicePayment,
    handleOpenInvoicePayment,
    handleCloseInvoicePayment,
    openAdvPayment,
    handleOpenAdvPayment,
    handleCloseAdvPayment,
    handleOpenTagManagementDlg,
    handleCloseTagManagementDlg,
    handleClearSelection,
    handleGridSelection,
    handleSearch,
    handleClearSearch,
    loading,
    setLoading,
    filteredRows,
    searchText,
    data,
    paginationText,
    paginationModel,
    setPaginationModel,
    handlePaginationModel,
    selectedRows,
    rowSelectionModel,
    setRowSelectionModel,
    openTagManagementDlg,
    tagManagementData,
    fetchPayments,
    selectedPaymentRow
  };

  const RefundProps = {
    openRefundDlg,
    setOpenRefundDlg,
    refundData,
    setRefundData,
    handleOpenRefundDlg,
    handleCloseRefundDlg,
    openPaymentReceipt,
    handleOpenPaymentReceipt,
    handleClosePaymentReceipt,
  };
  //#endregion Props

  return {
    IndexProps,
    RefundProps,
  };
};
